import "./AsignarCiclos.css";
import { useQuery } from "@tanstack/react-query";
import { sendAjaxRequest } from "@/ts/api/comunicacion";
import { Disclosure } from "@/components/Disclosure";
import { ModalOFs } from "./components/ModalOFs";
import { ModalSeleccionarPersonal } from "./components/ModalSeleccionarPersonal";
import { ModalCicloNotificacion } from "./components/ModalCicloNotificacion";
import { FiltroCiclos } from "./components/FiltroCiclos";
import { useMemo, useState } from "react";
import { Breadcrumb } from "@/components/Breadcrumb";
import { useDisclosureData } from "@/hooks/useModal";
import { ModalCrearOF } from "@/components/modals/CrearOF/CrearOF";
import { Loading, QueryError } from "@/components/Loading/Loading";

export function AsignarCiclosPage() {
	const [newCiclosShown, setNewCiclosShown] = useState(false);
	const [filtroCiclo, setFiltroCiclo] = useState<string | null>(null);

	return (
		<div id="content" className="content" >
			<title>Piher | Asignar Ciclos</title>
			<div>

				<Breadcrumb
					sectionTitle="Asignar Ciclos"
					links={[
						{ label: "Asignar Ciclos", active: true }
					]}
				/>


				<Disclosure style={{ marginBottom: 20 }}>
					{/* First element is the summary, second is the content */}
					<h4 className="panel-title panel-heading senalar">
						Filtro
					</h4>
					<FiltroCiclos filtroCiclo={filtroCiclo} setFiltroCiclo={setFiltroCiclo} />
				</Disclosure>


				<div className="table-responsive">
					<TablaCiclos filtroCiclo={filtroCiclo} />
				</div>
			</div>

			<ModalCicloNotificacion show={newCiclosShown} close={() => { setNewCiclosShown(false); }} />
		</div>
	);
}


function TablaCiclos(props: { readonly filtroCiclo: string | null; }) {
	const discloseVerOrdenes = useDisclosureData<string | null>(null);
	const discloseCrearOrden = useDisclosureData<string | null>(null);
	const discloseSeleccionarPersonal = useDisclosureData<number | null>(null);

	const { data, error, isPending } = useArticulosCiclos();
	const { data: dataAsignados, error: errorAsignados, isPending: isPendingAsignados } = useCiclosAsignados();

	const ciclosVisualizados = useMemo(() => {
		if (data == null) return [];

		const ciclos = props.filtroCiclo == null ?
			Object.entries(data) :
			Object.entries(data).filter(([idCiclo]) => idCiclo === props.filtroCiclo);

		return ciclos.sort((itemA, itemB) => Number(itemA[0]) - Number(itemB[0]));
	}, [data, props.filtroCiclo]);

	if (error || errorAsignados) return <QueryError error={error ?? errorAsignados} fallback="Error al cargar los datos" />;
	if (isPending || isPendingAsignados) return <Loading />;

	return (
		<>
			<table className="display dataTable" style={{ textAlign: "center" }}>
				<thead>
					<LineaHeader />
				</thead>
				<tbody>
					{ciclosVisualizados.map(([idCiclo, articulos]) => (
						<LineaCiclo
							key={idCiclo}
							idCiclo={idCiclo}
							articulos={articulos}
							isAsignado={dataAsignados.includes(Number(idCiclo))}
							openVerOrdenes={discloseVerOrdenes.open}
							openCrearOrden={discloseCrearOrden.open}
							openSeleccionarPersonal={discloseSeleccionarPersonal.open}
						/>
					))}
				</tbody>
			</table>
			<ModalOFs
				xconjunto_id={discloseVerOrdenes.data}
				show={discloseVerOrdenes.isOpen}
				close={discloseVerOrdenes.close}
			/>
			<ModalCrearOF
				idArticulo={discloseCrearOrden.data}
				show={discloseCrearOrden.isOpen}
				close={discloseCrearOrden.close}
			/>
			<ModalSeleccionarPersonal
				idCiclo={discloseSeleccionarPersonal.data}
				show={discloseSeleccionarPersonal.isOpen}
				close={discloseSeleccionarPersonal.close}
			/>
		</>
	);
}

function LineaHeader() {
	return (
		<tr>
			<th className="dt-center">Ciclo</th>

			<th className="dt-center md:hidden">Familia</th>
			<th className="dt-center md:hidden">SubFamilia</th>
			<th className="dt-center sm:hidden">Artículo</th>

			<th className="dt-center">Pedido</th>
			<th className="dt-center">Stock</th>

			<th className="dt-center lg:cell hidden sm:hidden">Min/Max</th>
			<th className="dt-center lg:hidden">Mínimo</th>
			<th className="dt-center lg:hidden">Máximo</th>

			<th className="dt-center lg:cell hidden">Pte. Ent/Sal</th>
			<th className="dt-center lg:hidden">Pte. Entrada</th>
			<th className="dt-center lg:hidden">Pte. Salida</th>
			<th className="dt-center">Fabricar</th>

			<th />
		</tr>
	);
}
interface LineaCicloProps {
	readonly idCiclo: string,
	readonly articulos: Record<string, ArticulosFabricaLinea>;
	readonly isAsignado: boolean;
	readonly openVerOrdenes: (idArticulo: string) => void;
	readonly openCrearOrden: (idArticulo: string) => void;
	readonly openSeleccionarPersonal: (idCiclo: number) => void;
}
function LineaCiclo(props: LineaCicloProps) {
	const { idCiclo, articulos, openVerOrdenes, openCrearOrden, openSeleccionarPersonal } = props;

	const icon = props.isAsignado ? "/img/icons/clipboard-user-clicked.svg" : "/img/icons/clipboard-user.svg";
	return (
		<>
			<tr className="lineaCiclo">
				<td >{idCiclo}</td>
				<td className="sm:hidden" />
				<td className="md:hidden" />
				<td className="md:hidden" />

				<td colSpan={3} />

				<td className="lg:cell hidden sm:hidden" />
				<td className="lg:hidden" />
				<td className="lg:hidden" />

				<td className="lg:cell hidden" />
				<td className="lg:hidden" />
				<td className="lg:hidden" />

				<td>
					<button className="btnIcon" onClick={() => { openSeleccionarPersonal(Number(idCiclo)); }} type="button">
						<img className="square35" src={icon} />
					</button>
				</td>
			</tr>
			{Object.values(articulos).map((linea) => (
				<LineaArticulo key={linea.idArticulo} linea={linea} openVerOrdenes={openVerOrdenes} openCrearOrden={openCrearOrden} />
			))}
		</>
	);
}
interface LineaArticuloProps { readonly linea: ArticulosFabricaLinea; readonly openVerOrdenes: (idArticulo: string) => void; readonly openCrearOrden: (idArticulo: string) => void; }
function LineaArticulo(props: LineaArticuloProps) {
	const { linea, openCrearOrden, openVerOrdenes } = props;
	const format = (num: number | null) => num == null ? "" : Intl.NumberFormat("es-ES", { maximumFractionDigits: 2, minimumFractionDigits: 0 }).format(num);

	return (
		<tr className="lineaArticulo">
			<td className="sm:hidden" />
			<td className="md:hidden">{linea.nombreFamilia}</td>
			<td className="md:hidden">{linea.nombreSubFamilia}</td>
			<td>{linea.idArticulo} - {linea.nombreArticulo}</td>

			<td>{format(linea.pedido)}</td>
			<td>{format(linea.stock)}</td>

			<td className="lg:cell hidden sm:hidden">{format(linea.min)}/{format(linea.max)}</td>
			<td className="lg:hidden">{format(linea.min)}</td>
			<td className="lg:hidden">{format(linea.max)}</td>


			<td className="lg:cell hidden">
				{linea.rutaArticulo != null && (
					<button className="link" onClick={() => { openVerOrdenes(linea.idArticulo); }} type="button">
						{format(linea.pendienteEntrada)}
					</button>
				)}
				{linea.rutaArticulo == null && format(linea.pendienteEntrada)}
				/
				{format(linea.pendienteSalida)}
			</td>
			<td className="lg:hidden">
				{linea.rutaArticulo != null && (
					<button className="link" onClick={() => { openVerOrdenes(linea.idArticulo); }} type="button">
						{format(linea.pendienteEntrada)}
					</button>
				)}
				{linea.rutaArticulo == null && format(linea.pendienteEntrada)}

			</td>
			<td className="lg:hidden">{format(linea.pendienteSalida)}</td>

			<td>{format(linea.entrarRecomendado)}</td>

			<td className="p-0 h-full">
				{linea.rutaArticulo != null && (
					<button className="clickable tableButton" type="button" onClick={() => { openCrearOrden(linea.idArticulo); }}>
						<i className="fa fa-plus" />
					</button>
				)}
			</td>
		</tr>
	);



}



function useCiclosAsignados() {
	return useQuery<number[]>({
		queryKey: ["getCiclosAsignados"],
		queryFn: () => {
			return sendAjaxRequest<number[]>("GET", "/api/v1/getCiclosAsignados");
		},
	});
}
function useArticulosCiclos() {
	return useQuery<Record<number, Record<string, ArticulosFabricaLinea>>>({
		queryKey: ["getArticulosCiclos"],
		queryFn: () => {
			return sendAjaxRequest<Record<number, Record<string, ArticulosFabricaLinea>>>("GET", "/api/v1/getArticulosCiclos");
		},
	});
}
interface ArticulosFabricaLinea {
	idCiclo: string,
	idMedio: string,
	nombreMedio: string,
	idPedido: string,

	nombreFamilia: string | null,
	nombreSubFamilia: string | null,
	idArticulo: string,
	nombreArticulo: string,
	rutaArticulo: string | null,

	min: number | null,
	max: number | null,
	stock: number | null,
	pendienteEntrada: number | null,
	pendienteSalida: number | null,
	entrarRecomendado: number | null,
	pedido: number | null,
}
